import { useCallback, useEffect, useRef, useState } from 'react';
import { GuestCall } from '@/components/GuestCall';
import { sframeSupported } from '@/hooks/sframe';

/**
 * Welle 8d — Gast-/Experten-App (Magic-Link).
 *
 * Eigener Top-Level-Branch (via ?gast in main.tsx), komplett losgelöst von der
 * Berater-App: kein Login, keine Agent-Hooks, kein Layout-Chrome. Mobil- und
 * iPad-tauglich (kein App-Download nötig).
 *
 * Ablauf:
 *   resolving → ready (Namenseingabe) → joining
 *     → waiting  (kein Gespräch aktiv → erneut anfragen)
 *     → pending  (Berater klingelt → Status pollen)
 *       → admitted (rtcToken erhalten — A/V folgt im Mesh-Increment)
 *       → declined
 *
 * Der Token steckt im URL-Fragment (#t=…), nicht in der Query — so wird er nicht
 * an den Server geloggt oder im Referer mitgeschickt.
 */

type Phase =
  | 'resolving'
  | 'invalid'
  | 'ready'
  | 'joining'
  | 'waiting'
  | 'pending'
  | 'admitted'
  | 'calling'
  | 'no-advisers'
  | 'declined'
  | 'error';

interface ResolveResult {
  valid: boolean;
  reason?: string;
  kind?: 'call' | 'agent' | 'group';
  tenantName?: string;
  target?: string;
  expectedName?: string;
  validFrom?: string;
  validUntil?: string;
}

/** "Mittwoch, 11. Juni · 14:00–15:00 Uhr" bzw. nur Beginn, wenn kein Ende. */
function formatAppointment(from?: string, until?: string): string | null {
  if (!from) return null;
  const f = new Date(from);
  if (isNaN(f.getTime())) return null;
  const day = f.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
  const t = (d: Date) => d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const u = until ? new Date(until) : null;
  if (u && !isNaN(u.getTime())) return `${day} · ${t(f)}–${t(u)} Uhr`;
  return `${day} · ${t(f)} Uhr`;
}

function readToken(): string | null {
  const hash = window.location.hash.replace(/^#/, '');
  const fromHash = new URLSearchParams(hash).get('t');
  if (fromHash) return fromHash;
  // Fallback: ?t= in der Query (falls jemand den Link umbaut)
  return new URLSearchParams(window.location.search).get('t');
}

const REASON_TEXT: Record<string, string> = {
  'not-found': 'Dieser Link ist ungültig oder wurde nicht gefunden.',
  revoked: 'Dieser Link wurde zurückgezogen und ist nicht mehr gültig.',
  expired: 'Dieser Link ist abgelaufen.',
  'too-early': 'Dieser Link ist noch nicht freigeschaltet.',
};

export default function GuestApp() {
  const token = readToken();
  // pre91: Direkt-Anruf-Modus. ?direct gesetzt → Gruppenlink ruft ohne Buttons direkt an,
  // optional ?name=… als Anzeigename. Nach dem Call wird das Fenster geschlossen.
  const directMode = new URLSearchParams(window.location.search).has('direct');
  const urlName = new URLSearchParams(window.location.search).get('name') || '';
  // pre92: E2EE nur wenn per Link angefordert (?e2ee) UND der Browser SFrame kann.
  // Auf iOS/Safari (kein Insertable-Streams-Support) bleibt es so robust aus.
  const wantE2ee = new URLSearchParams(window.location.search).has('e2ee') && sframeSupported();
  const autoStartedRef = useRef(false);
  const [phase, setPhase] = useState<Phase>('resolving');
  const [info, setInfo] = useState<ResolveResult | null>(null);
  const [name, setName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [admittedToken, setAdmittedToken] = useState<string | null>(null);
  const [callerMode, setCallerMode] = useState(false);
  const requestIdRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // ── Token auflösen ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setInfo({ valid: false, reason: 'not-found' });
      return;
    }
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`/api/links/${encodeURIComponent(token)}`);
        const data: ResolveResult = await res.json();
        if (!alive) return;
        setInfo(data);
        if (data.valid) {
          if (data.expectedName) setName(data.expectedName);
          setPhase('ready');
        } else {
          setPhase('invalid');
        }
      } catch {
        if (alive) {
          setErrorMsg('Verbindung zum Server fehlgeschlagen.');
          setPhase('error');
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // ── Beitritt anfragen ──────────────────────────────────────────────────────
  const requestJoin = useCallback(
    async (silent = false, nameOverride?: string) => {
      if (!token) return;
      if (!silent) setPhase('joining');
      const effName = (nameOverride ?? name).trim();
      try {
        const res = await fetch(`/api/links/${encodeURIComponent(token)}/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: effName, e2ee: wantE2ee }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setErrorMsg(body?.message || 'Beitritt nicht möglich.');
          setPhase('error');
          return;
        }
        const data = await res.json();
        if (data.status === 'pending') {
          requestIdRef.current = data.requestId;
          setPhase('pending');
        } else if (data.status === 'waiting') {
          setPhase('waiting');
        } else if (data.status === 'calling') {
          // pre90: Gruppenlink → eigener eingehender Call. Gast ist Anrufer (role='stele').
          (window as any).__guestRtc = { rtcToken: data.rtcToken, callId: data.callId, name: effName, caller: true };
          setAdmittedToken(data.rtcToken);
          setCallerMode(true);
          setPhase('calling');
        } else if (data.status === 'no-advisers') {
          setPhase('no-advisers');
        } else if (data.status === 'busy') {
          // Welle 8d/M2: Einzel-Slot — es ist bereits ein Experte zugeschaltet.
          setErrorMsg('Es ist bereits ein Experte zugeschaltet. Aktuell ist nur ein Experte gleichzeitig möglich — bitte später erneut versuchen.');
          setPhase('error');
        }
      } catch {
        setErrorMsg('Verbindung zum Server fehlgeschlagen.');
        setPhase('error');
      }
    },
    [token, name, wantE2ee],
  );

  // ── pre91: Direkt-Anruf (?direct) — Gruppenlink ohne Buttons sofort anrufen ─
  useEffect(() => {
    if (phase !== 'ready' || !directMode || autoStartedRef.current) return;
    if (info?.kind !== 'group') return; // Direkt-Anruf nur für Gruppenlinks
    autoStartedRef.current = true;
    const n = (urlName || info?.expectedName || 'Gast').trim();
    setName(n);
    void requestJoin(false, n);
  }, [phase, directMode, info, urlName, requestJoin]);

  // ── Warteraum: erneut anfragen, bis ein Gespräch läuft ─────────────────────
  useEffect(() => {
    if (phase !== 'waiting') return;
    clearTimer();
    timerRef.current = setTimeout(() => requestJoin(true), 3000);
    return clearTimer;
  }, [phase, requestJoin]);

  // ── Pending: Freigabe-Status pollen ────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'pending') return;
    let alive = true;
    const poll = async () => {
      const reqId = requestIdRef.current;
      if (!reqId) return;
      try {
        const res = await fetch(`/api/links/join/${encodeURIComponent(reqId)}/status`);
        const data = await res.json();
        if (!alive) return;
        if (data.status === 'admitted') {
          // rtcToken für den Mesh-Beitritt übernehmen.
          (window as any).__guestRtc = {
            rtcToken: data.rtcToken,
            callId: data.callId,
            e2ee: data.e2ee,
            name: name.trim(),
          };
          setAdmittedToken(data.rtcToken);
          clearTimer();
          setPhase('admitted');
        } else if (data.status === 'declined') {
          clearTimer();
          setPhase('declined');
        } else if (data.status === 'expired') {
          // Anfrage abgelaufen → zurück in den Warteraum, neu anfragen.
          requestIdRef.current = null;
          setPhase('waiting');
        } else {
          timerRef.current = setTimeout(poll, 2000);
        }
      } catch {
        if (alive) timerRef.current = setTimeout(poll, 3000);
      }
    };
    poll();
    return () => {
      alive = false;
      clearTimer();
    };
  }, [phase, name]);

  useEffect(() => () => clearTimer(), []);

  // ── Render ─────────────────────────────────────────────────────────────────
  // Zugelassen/Anruf läuft + Token vorhanden → direkt in die Call-Ansicht (Vollbild).
  if ((phase === 'admitted' || phase === 'calling') && admittedToken) {
    return <GuestCall rtcToken={admittedToken} caller={callerMode} autoClose={directMode} />;
  }

  const canJoin = name.trim().length >= 2;

  return (
    <div className="ga-root">
      <style>{GUEST_CSS}</style>
      <div className="ga-card">
        <div className="ga-brand">
          <div className="ga-logo">bamoa</div>
          <div className="ga-sub">Videoservice</div>
        </div>

        {phase === 'resolving' && (
          <div className="ga-body">
            <div className="ga-spinner" />
            <p className="ga-muted">Link wird geprüft…</p>
          </div>
        )}

        {phase === 'invalid' && info?.reason === 'too-early' && (
          <div className="ga-body">
            <div className="ga-icon ga-icon-ok">🕑</div>
            <h1>Gespräch noch nicht gestartet</h1>
            <p className="ga-muted">
              {formatAppointment(info.validFrom)
                ? <>Dieser Termin beginnt am <strong>{formatAppointment(info.validFrom, info.validUntil)}</strong>. Bitte rufen Sie den Link kurz vorher erneut auf.</>
                : 'Dieser Link ist noch nicht freigeschaltet. Bitte versuchen Sie es später erneut.'}
            </p>
          </div>
        )}

        {phase === 'invalid' && info?.reason !== 'too-early' && (
          <div className="ga-body">
            <div className="ga-icon ga-icon-warn">!</div>
            <h1>Link nicht gültig</h1>
            <p className="ga-muted">{REASON_TEXT[info?.reason || 'not-found'] || 'Dieser Link kann nicht verwendet werden.'}</p>
          </div>
        )}

        {phase === 'no-advisers' && (
          <div className="ga-body">
            <div className="ga-icon ga-icon-warn">!</div>
            <h1>Aktuell niemand erreichbar</h1>
            <p className="ga-muted">
              Im Moment ist kein Berater verfügbar, um Ihren Anruf anzunehmen. Bitte versuchen Sie es in
              Kürze erneut.
            </p>
            <button className="ga-btn ga-btn-ghost" onClick={() => requestJoin()}>
              Erneut anrufen
            </button>
          </div>
        )}

        {phase === 'error' && (
          <div className="ga-body">
            <div className="ga-icon ga-icon-warn">!</div>
            <h1>Etwas ist schiefgelaufen</h1>
            <p className="ga-muted">{errorMsg}</p>
            <button className="ga-btn ga-btn-ghost" onClick={() => window.location.reload()}>
              Erneut versuchen
            </button>
          </div>
        )}

        {(phase === 'ready' || phase === 'joining') && info?.valid && (
          <div className="ga-body">
            <h1>Gespräch beitreten</h1>
            <p className="ga-muted">
              Sie treten {info.tenantName ? <strong>{info.tenantName}</strong> : 'dem Videoservice'} bei
              {info.target ? <> · {info.target}</> : null}.
            </p>
            <label className="ga-label" htmlFor="ga-name">
              Ihr Name
            </label>
            <input
              id="ga-name"
              className="ga-input"
              type="text"
              autoComplete="name"
              placeholder="z. B. Dr. Müller"
              value={name}
              maxLength={80}
              disabled={phase === 'joining'}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canJoin && phase === 'ready') requestJoin();
              }}
            />
            <button className="ga-btn" disabled={!canJoin || phase === 'joining'} onClick={() => requestJoin()}>
              {phase === 'joining' ? 'Wird gesendet…' : 'Beitreten'}
            </button>
            <p className="ga-fine">
              Der Berater muss Ihren Beitritt bestätigen. Kamera und Mikrofon werden erst danach benötigt.
            </p>
          </div>
        )}

        {phase === 'waiting' && (
          <div className="ga-body">
            <div className="ga-spinner" />
            <h1>Das Gespräch hat noch nicht begonnen</h1>
            <p className="ga-muted">
              Ihr Ansprechpartner hat das Gespräch noch nicht gestartet. Sie werden automatisch verbunden,
              sobald es losgeht – Sie müssen nichts weiter tun und können dieses Fenster geöffnet lassen.
            </p>
            {formatAppointment(info?.validFrom, info?.validUntil) && (
              <p className="ga-fine">Geplanter Termin: {formatAppointment(info?.validFrom, info?.validUntil)}</p>
            )}
          </div>
        )}

        {phase === 'pending' && (
          <div className="ga-body">
            <div className="ga-spinner" />
            <h1>Anfrage gesendet</h1>
            <p className="ga-muted">
              Ihre Beitrittsanfrage wurde gestellt. Bitte warten Sie, bis der Berater Sie zulässt…
            </p>
          </div>
        )}

        {phase === 'admitted' && (
          <div className="ga-body">
            <div className="ga-icon ga-icon-ok">✓</div>
            <h1>Sie sind zugelassen</h1>
            <p className="ga-muted">
              Der Berater hat Ihren Beitritt bestätigt. Die Audio-/Video-Verbindung wird im nächsten Schritt
              hergestellt.
            </p>
          </div>
        )}

        {phase === 'declined' && (
          <div className="ga-body">
            <div className="ga-icon ga-icon-warn">×</div>
            <h1>Beitritt abgelehnt</h1>
            <p className="ga-muted">Der Berater hat Ihren Beitritt nicht zugelassen.</p>
          </div>
        )}
      </div>
      <div className="ga-foot">Bamoa GmbH · Verschlüsselte Verbindung</div>
    </div>
  );
}

const GUEST_CSS = `
.ga-root {
  position: fixed; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 18px; padding: 20px;
  background: radial-gradient(120% 120% at 50% 0%, #11343a 0%, #0a2125 55%, #06171a 100%);
  color: #e8f1f1;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}
.ga-card {
  width: 100%; max-width: 420px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 20px;
  padding: 28px 24px 30px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.45);
  backdrop-filter: blur(8px);
}
.ga-brand { text-align: center; margin-bottom: 22px; }
.ga-logo { font-size: 26px; font-weight: 700; letter-spacing: 0.5px; color: #5fd4c4; }
.ga-sub { font-size: 12px; letter-spacing: 2px; text-transform: uppercase; color: #7fa6a3; margin-top: 2px; }
.ga-body { text-align: center; display: flex; flex-direction: column; align-items: center; gap: 12px; }
.ga-body h1 { font-size: 21px; font-weight: 650; margin: 2px 0 0; }
.ga-muted { font-size: 15px; line-height: 1.5; color: #aecccb; margin: 0; }
.ga-fine { font-size: 12.5px; line-height: 1.45; color: #7fa6a3; margin: 4px 0 0; }
.ga-label { align-self: flex-start; font-size: 13px; color: #aecccb; margin: 10px 0 -4px; }
.ga-input {
  width: 100%; box-sizing: border-box;
  padding: 14px 16px; font-size: 17px;
  border-radius: 12px; border: 1px solid rgba(255,255,255,0.16);
  background: rgba(0,0,0,0.25); color: #fff; outline: none;
}
.ga-input:focus { border-color: #5fd4c4; box-shadow: 0 0 0 3px rgba(95,212,196,0.18); }
.ga-btn {
  width: 100%; margin-top: 14px; padding: 15px 18px;
  font-size: 16px; font-weight: 650; cursor: pointer;
  border: none; border-radius: 12px;
  background: #2bb6a3; color: #042; transition: background 0.15s, opacity 0.15s;
}
.ga-btn:hover:not(:disabled) { background: #36c9b5; }
.ga-btn:disabled { opacity: 0.45; cursor: default; }
.ga-btn-ghost { background: transparent; color: #aecccb; border: 1px solid rgba(255,255,255,0.16); }
.ga-icon {
  width: 56px; height: 56px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 30px; font-weight: 700; margin-bottom: 2px;
}
.ga-icon-ok { background: rgba(43,182,163,0.16); color: #5fd4c4; }
.ga-icon-warn { background: rgba(214,120,90,0.16); color: #f0a17f; }
.ga-spinner {
  width: 38px; height: 38px; border-radius: 50%;
  border: 3px solid rgba(255,255,255,0.15); border-top-color: #5fd4c4;
  animation: ga-spin 0.9s linear infinite; margin-bottom: 4px;
}
@keyframes ga-spin { to { transform: rotate(360deg); } }
.ga-foot { font-size: 11.5px; color: #5a807d; letter-spacing: 0.3px; }
`;
